import React from "react";
import { styles } from "../styles/theme";
import { formatMoney } from "../utils/helpers";

function PollSection({ 
  gameData, 
  playerData,
  isSubmittingVote, 
  handlePollVote, 
  pollVote,
  totalPlayers,
  totalVotes
}) {
  const voteLabels = {
    increase: "Aumentar ayuda",
    decrease: "Reducir ayuda",
  };

  // La encuesta está activa durante toda la ronda y el jugador debe estar activo
  const isPollActive = gameData.roundStatus === "open" && playerData?.active !== false;
  
  // Verificar si es la ronda final
  const isFinalRound = gameData.currentRound === gameData.totalRounds;
  
  // No mostrar la sección de votación en la ronda final
  if (isFinalRound) {
    return null;
  }
  
  const currentStipend = gameData.baseStipend 
    ? gameData.baseStipend * (gameData.stipendMultiplier || 1)
    : 10;

  // Verificar si todos han votado
  const allVoted = totalPlayers > 0 && totalVotes === totalPlayers;

  // Mensajes educativos por ronda cuando todos han votado
  const educationalMessages = {
    2: "Si todos reciben más dinero pero el pan sigue siendo el mismo, los precios suben. El valor no está en el billete, está en lo que puedes comprar con él.",
    3: "Imprimir más dinero no crea más pan. Solo reparte el mismo pan a precios más altos. La producción es lo que realmente importa.",
    4: "Si todos reciben más dinero sin haber más producción, cada billete vale menos. La ayuda no aumenta la riqueza: solo reparte el mismo valor entre más papel.",
    5: "La impresión descontrolada es la forma más silenciosa de cobrar impuestos: no aparece en tu recibo, pero se siente en cada compra.",
    6: "Cuando nadie sabe realmente cuánta moneda existe o quién la controla, los precios dejan de reflejar la realidad. La transparencia evita abusos y restaura la confianza."
  };

  return (
    <section style={bannerStyles.section}>
      <div style={bannerStyles.content}>
        {gameData.currentRound === 1 ? (
          <div style={bannerStyles.textSection}>
            <p style={bannerStyles.description}>
              El panadero hace pan cada 5 minutos y todos necesitamos comer. El precio es lo que el consumidor esté dispuesto a pagar.
            </p>
          </div>
        ) : gameData.currentRound === 6 ? (
          <div style={bannerStyles.textSection}>
            <p style={bannerStyles.description}>
              {educationalMessages[6]}
            </p>
          </div>
        ) : allVoted && isPollActive && educationalMessages[gameData.currentRound] ? (
          <div style={bannerStyles.textSection}>
            <p style={bannerStyles.description}>
              {educationalMessages[gameData.currentRound]}
            </p>
          </div>
        ) : (
          <>
            <div style={bannerStyles.textSection}>
              <p style={bannerStyles.description}>
                Los jugadores con menos dinero reciben <strong>{formatMoney(currentStipend)}</strong> de ayuda al final de cada ronda.
                <strong> ¿Qué quieres que pase con esa ayuda para la próxima ronda?</strong>
              </p>
            </div>
            
            {!isPollActive ? (
              <div style={bannerStyles.statusMessage}>
                <p style={bannerStyles.statusText}>
                  ⏳ La encuesta estará disponible en la siguiente ronda
                </p>
              </div>
            ) : pollVote ? (
              <div style={bannerStyles.voted}>
                <span style={bannerStyles.votedIcon}>✅</span>
                <div>
                  <p style={bannerStyles.votedText}>
                    Tu voto: <strong>{voteLabels[pollVote]}</strong>
                  </p>
                  <p style={bannerStyles.votedSubtext}>
                    {totalVotes}/{totalPlayers} jugadores han votado
                  </p>
                </div>
              </div>
            ) : (
              <div style={bannerStyles.buttonGroup}>
                <button
                  style={bannerStyles.voteButton}
                  disabled={isSubmittingVote}
                  onClick={() => handlePollVote("increase")}
                >
                  📈 Aumentar
                </button>
                <button
                  style={bannerStyles.voteButton}
                  disabled={isSubmittingVote}
                  onClick={() => handlePollVote("decrease")}
                >
                  📉 Reducir
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

const bannerStyles = {
  section: {
    background: "linear-gradient(135deg, rgba(0, 50, 100, 0.4), rgba(0, 20, 50, 0.3))",
    border: "2px solid rgba(0, 155, 255, 0.5)",
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 16,
    boxShadow: "0 4px 15px rgba(0, 155, 255, 0.3)",
  },
  content: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    flexWrap: "wrap",
  },
  textSection: {
    flex: "1 1 300px",
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#5DB6FF",
    margin: "0 0 6px 0",
  },
  description: {
    fontSize: 13,
    color: "#E8F6FF",
    lineHeight: 1.5,
    margin: 0,
  },
  buttonGroup: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  voteButton: {
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    color: "#E8F6FF",
    background: "linear-gradient(135deg, rgba(0, 155, 255, 0.3), rgba(0, 100, 200, 0.2))",
    border: "2px solid rgba(0, 155, 255, 0.5)",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.3s ease",
    whiteSpace: "nowrap",
  },
  voted: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  votedIcon: {
    fontSize: 32,
  },
  votedText: {
    fontSize: 15,
    color: "#5DB6FF",
    fontWeight: 600,
    margin: 0,
  },
  votedSubtext: {
    fontSize: 12,
    color: "#A9D9FF",
    margin: "2px 0 0 0",
  },
  statusMessage: {
    display: "flex",
    alignItems: "center",
  },
  statusText: {
    fontSize: 14,
    color: "#A9D9FF",
    fontStyle: "italic",
    margin: 0,
  },
};

export default PollSection;
